import { useState, type ReactNode } from "react";
import { Drawer } from "./Drawer";
import { ThemeToggle } from "../ThemeToggle";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useIsTouch } from "../../hooks/useIsTouch";
import styles from "./TopBar.module.css";

interface TopBarProps {
  /** Brand mark — typically the wordmark or logo. Always visible. */
  brand: ReactNode;
  /**
   * Primary content shown adjacent to the brand on desktop.
   * On phone/tablet this content collapses behind a hamburger and
   * becomes the top section of the drawer body (above `drawerContent`).
   */
  primary?: ReactNode;
  /**
   * Drawer-only content. Rendered exclusively inside the drawer on
   * phone/tablet, never on desktop. Use for navigation that doesn't fit
   * in the topbar (e.g., the full LeftRail).
   */
  drawerContent?: ReactNode;
  /**
   * Trailing actions on the right of the topbar (e.g., settings icon).
   * Always visible at every breakpoint. Items with `data-touch-hide`
   * (e.g., ⌘K + ? buttons) are hidden when the primary pointer is touch.
   */
  trailing?: ReactNode;
  /** Aria label for the drawer dialog. Defaults to "Navigation menu". */
  drawerAriaLabel?: string;
}

/**
 * The app's topbar primitive. Resolves desktop vs. drawer behavior so
 * pages and shells don't have to think about it.
 *
 * Layout:
 *   Desktop  : [brand] [primary]  ─────  [trailing]
 *   Tablet+  : [≡] [brand]        ─────  [trailing-not-touch] [trailing]
 *   Phone    : same as tablet
 *
 * Touch-only pointer devices receive a `data-touch="true"` attribute on
 * the topbar root; CSS hides any trailing item marked `data-touch-hide`.
 * Drives the "⌘K and ? button vanish on phone" rule from the design.
 */
export function TopBar({
  brand,
  primary,
  drawerContent,
  trailing,
  drawerAriaLabel = "Navigation menu",
}: TopBarProps) {
  const breakpoint = useBreakpoint();
  const isTouch = useIsTouch();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isDesktop = breakpoint === "desktop";
  const hasDrawerContent = Boolean(primary || drawerContent);

  return (
    <>
      <header
        className={styles.topbar}
        data-touch={isTouch ? "true" : "false"}
        data-breakpoint={breakpoint}
      >
        {!isDesktop && hasDrawerContent ? (
          <button
            type="button"
            className={styles.hamburger}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
            aria-controls="sf-drawer"
          >
            <span className={styles.hamburgerBar} aria-hidden="true" />
            <span className={styles.hamburgerBar} aria-hidden="true" />
            <span className={styles.hamburgerBar} aria-hidden="true" />
          </button>
        ) : null}

        <div className={styles.brand}>{brand}</div>

        {isDesktop && primary ? <div className={styles.primary}>{primary}</div> : null}

        <div className={styles.spacer} />

        <div className={styles.trailing}>
          <ThemeToggle />
          {trailing}
        </div>
      </header>

      {hasDrawerContent && !isDesktop ? (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ariaLabel={drawerAriaLabel}
        >
          <div id="sf-drawer" className={styles.drawerInner}>
            <div className={styles.drawerHeader}>
              <div className={styles.drawerBrand}>{brand}</div>
              <button
                type="button"
                className={styles.drawerClose}
                onClick={() => setDrawerOpen(false)}
                aria-label="Close navigation menu"
              >
                &times;
              </button>
            </div>
            {primary ? <div className={styles.drawerPrimary}>{primary}</div> : null}
            {drawerContent ? <div className={styles.drawerBody}>{drawerContent}</div> : null}
          </div>
        </Drawer>
      ) : null}
    </>
  );
}
